import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';
import { useSupabase } from '../../../contexts/SupabaseContext';
import GuidedTour from '../../../components/tour/GuidedTour';
import TourButton from '../../../components/tour/TourButton';
import useTour from '../../../components/tour/useTour';
import { TOURS } from '../../../components/tour/tours';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';

// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};
// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f',
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b',
];

// Update getYahooFinanceUrl function to handle mutual fund symbols
const getYahooFinanceUrl = (symbol) => {
  // For mutual funds, we'll use the symbol directly
  // No need to add .NS or .BO as mutual funds have their own format
  const yahooUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${symbol}?interval=1d&range=1d`;
  return `/api/yahoo-chart?url=${encodeURIComponent(yahooUrl)}`;
};

const MutualFund = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-cash-and-liquid-mutual-fund', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [fundTransactions, setFundTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editNavValue, setEditNavValue] = useState('');
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editUnits, setEditUnits] = useState(0);
  
  // Form state
  const [fundName, setFundName] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [navValue, setNavValue] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [units, setUnits] = useState(0);
  
  // User's saved fund names (will be populated from existing investments)
  const [userFundNames, setUserFundNames] = useState([]);

  // Add new state variables for live prices
  const [livePrices, setLivePrices] = useState({});
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  const [priceError, setPriceError] = useState(null);
  const [currentTotalValue, setCurrentTotalValue] = useState(0);

  // Add symbol-related state variables
  const [fundSymbol, setFundSymbol] = useState('');
  const [editFundSymbol, setEditFundSymbol] = useState('');
  const [openSymbolModal, setOpenSymbolModal] = useState(false);
  const [selectedFundForSymbol, setSelectedFundForSymbol] = useState(null);
  const [newSymbol, setNewSymbol] = useState('');

  // Add sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  // Dummy data for Mutual Funds
  const dummyData = [
    { id: 1, name: 'Mutual Fund 1', value: 100000 },
    { id: 2, name: 'Mutual Fund 2', value: 75000 },
    { id: 3, name: 'Mutual Fund 3', value: 50000 },
  ];

  // Fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch mutual fund investments');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('Fetching mutual fund investments for user ID:', user.id);
      
      // Fetch from Supabase
      const { data, error } = await supabase
        .from('mutual_fund_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        console.error('Error fetching mutual fund investments:', error);
        throw error;
      }
      
      console.log('Fetched mutual fund data:', data);
      
      // Process data - format and group by fund name
      if (data && data.length > 0) {
        // Extract unique fund names for autocomplete
        const uniqueFundNames = [...new Set(data.map(item => item.name))];
        setUserFundNames(uniqueFundNames);
        
        // Format data for display
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          value: item.amount_invested,
          navValue: item.nav_value,
          purchaseDate: item.purchase_date,
          units: item.units,
          symbol: item.symbol
        }));
        
        // Group investments by fund name
        const groupedInvestments = groupInvestmentsByFund(formattedData);
        setInvestments(groupedInvestments);
        
        // Calculate total value
        const total = groupedInvestments.reduce((sum, fund) => sum + fund.value, 0);
        setTotalValue(total);
        console.log('Total mutual fund value:', total);
      } else {
        console.log('No mutual fund investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserFundNames([]);
      }
    } catch (error) {
      console.error('Error processing mutual fund investments:', error);
      // Fall back to empty state
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);
  
  // Group investments by fund name
  const groupInvestmentsByFund = (investments) => {
    const fundMap = new Map();
    
    investments.forEach(investment => {
      const { name, value, units, symbol } = investment;
      
      if (fundMap.has(name)) {
        const fund = fundMap.get(name);
        fund.value += value;
        fund.units += parseFloat(units || 0);
        // If the fund doesn't have a symbol yet, add it from the current investment if available
        if (!fund.symbol && symbol) {
          fund.symbol = symbol;
        }
        fund.transactions.push(investment);
      } else {
        fundMap.set(name, {
          name,
          symbol: symbol || null, // Include symbol when creating the fund entry
          value,
          units: parseFloat(units || 0),
          transactions: [investment]
        });
      }
    });
    
    // Convert map to array
    return Array.from(fundMap.values());
  };
  
  // Auto-calculate units when NAV and amount change
  useEffect(() => {
    if (navValue && amountInvested && parseFloat(navValue) > 0) {
      setUnits((parseFloat(amountInvested) / parseFloat(navValue)).toFixed(3));
    } else {
      setUnits('');
    }
  }, [navValue, amountInvested]);
  
  // Auto-calculate units for edit form
  useEffect(() => {
    if (editNavValue && editAmountInvested && parseFloat(editNavValue) > 0) {
      setEditUnits((parseFloat(editAmountInvested) / parseFloat(editNavValue)).toFixed(3));
    } else {
      setEditUnits('');
    }
  }, [editNavValue, editAmountInvested]);
  
  // Load data on component mount
  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);
  
  // Modal handlers
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);
  
  const handleReset = () => {
    setFundName('');
    setFundSymbol('');
    setPurchaseDate(new Date());
    setNavValue('');
    setAmountInvested('');
    setUnits('');
  };
  
  // Transaction history modal handlers
  const handleOpenTransactionHistory = (fund) => {
    setSelectedFund(fund);
    setFundTransactions(fund.transactions || []);
    setOpenHistoryModal(true);
  };
  
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedFund(null);
    setFundTransactions([]);
  };
  
  // Transaction action handlers
  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Edit transaction handlers
  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    // Set form values from selected transaction
    try {
      if (currentTransaction) {
        console.log('Setting edit form with transaction:', currentTransaction);
        setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
        setEditNavValue(currentTransaction.navValue?.toString() || '');
        setEditAmountInvested(currentTransaction.value?.toString() || '');
        setEditUnits(currentTransaction.units?.toString() || '');
        setOpenEditModal(true);
      } else {
        console.error('No transaction selected for edit');
      }
    } catch (error) {
      console.error('Error preparing edit form:', error, 'Current transaction:', currentTransaction);
    }
  };
  
  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setEditPurchaseDate(null);
    setEditNavValue('');
    setEditAmountInvested('');
    setEditUnits('');
  };
  
  // Delete transaction handlers
  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const pieChartData = useMemo(() => {
    const currentData = investments.map((investment) => {
      const livePrice = livePrices[investment.name]?.price;
      const currentValue = livePrice 
        ? investment.units * livePrice 
        : investment.value;

      return {
        id: investment.id,
        value: currentValue,
        label: investment.name,
        percentage: ((currentValue / currentTotalValue) * 100).toFixed(1) + '%'
      };
    }).sort((a, b) => b.value - a.value);

    const investedData = investments.map((investment) => ({
      id: investment.id,
      value: investment.value,
      label: investment.name,
      percentage: ((investment.value / totalValue) * 100).toFixed(1) + '%'
    })).sort((a, b) => b.value - a.value);

    return { currentData, investedData };
  }, [investments, livePrices, currentTotalValue, totalValue]);

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    
    if (!fundName || !purchaseDate || !navValue || !amountInvested || !fundSymbol) {
      alert('Please fill all required fields including the symbol code');
      return;
    }

    try {
      setLoading(true);
      
      // Get the fund name (either from string input or from selected option)
      const fundNameValue = typeof fundName === 'string' ? fundName : fundName.label || fundName;
      
      // Prepare the data for Supabase - only include fields that exist in the schema
      const newInvestment = {
        name: fundNameValue,
        fund_name: fundNameValue, // Add fund_name field since the query looks for it
        symbol: fundSymbol.trim().toUpperCase(), // Store symbol in uppercase
        purchase_date: purchaseDate.toISOString().split('T')[0],
        nav_value: parseFloat(navValue),
        amount_invested: parseFloat(amountInvested),
        units: parseFloat(units),
        user_id: user.id // Make sure to include user_id
      };

      console.log('Creating new mutual fund investment:', newInvestment);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('mutual_fund_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      // Add the new investment to the list and update total value
      if (data && data.length > 0) {
        // Refresh the data from Supabase
        await fetchInvestments();
      }

      // Reset form and close modal
      handleReset();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTransaction = async () => {
    if (!editNavValue || !editAmountInvested || !editPurchaseDate) {
      alert('Please fill all required fields');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare the data for Supabase - ensure proper data types
      const updatedTransaction = {
        name: currentTransaction.name,
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0] 
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        nav_value: parseFloat(editNavValue),
        amount_invested: parseFloat(editAmountInvested),
        units: parseFloat(editUnits),
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      // Update data in Supabase
      const { data, error } = await supabase
        .from('mutual_fund_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedFund && openHistoryModal) {
        // Get the latest transactions for this fund
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('mutual_fund_investments')
          .select('*')
          .eq('name', selectedFund.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError && refreshedTransactions) {
          const formattedTransactions = refreshedTransactions.map(item => ({
            id: item.id,
            name: item.name,
            value: item.amount_invested,
            navValue: item.nav_value,
            purchaseDate: item.purchase_date,
            units: item.units
          }));
          
          setFundTransactions(formattedTransactions);
        }
      }

      // Close the edit modal
      handleEditModalClose();
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      console.log('Deleting transaction:', currentTransaction.id);
      
      // Delete from Supabase
      const { error } = await supabase
        .from('mutual_fund_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedFund && openHistoryModal) {
        // Get the latest transactions for this fund
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('mutual_fund_investments')
          .select('*')
          .eq('name', selectedFund.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              value: item.amount_invested,
              navValue: item.nav_value,
              purchaseDate: item.purchase_date,
              units: item.units
            }));
            
            setFundTransactions(formattedTransactions);
          } else {
            // No transactions left for this fund, close the modal
            handleCloseHistoryModal();
          }
        }
      }

      // Close the delete dialog
      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Update fetchLivePrices function
  const fetchLivePrices = useCallback(async () => {
    setPriceError(null);
    setLivePriceLoading(true);
    
    try {
      console.log('Starting fetchLivePrices for mutual funds...');

      // Get unique fund symbols from investments that have symbols
      const fundsWithSymbols = investments.filter(inv => inv.symbol && inv.symbol.trim() !== '');
      const uniqueSymbols = [...new Set(fundsWithSymbols.map(inv => inv.symbol.trim()))];
      
      console.log('Unique fund symbols to fetch:', uniqueSymbols);

      if (uniqueSymbols.length === 0) {
        console.log('No valid symbols found to fetch prices');
        setPriceError('No valid symbols found. Please add symbol codes to your investments.');
        return;
      }

      const newPrices = {};
      let hasValidPrices = false;

      // Process symbols in batches to avoid rate limiting
      const batchSize = 2;
      for (let i = 0; i < uniqueSymbols.length; i += batchSize) {
        const batch = uniqueSymbols.slice(i, i + batchSize);
        console.log(`Processing batch ${i/batchSize + 1}:`, batch);
        
        try {
          // Add delay between batches to avoid rate limiting
          if (i > 0) {
            console.log('Adding delay between batches...');
            await new Promise(resolve => setTimeout(resolve, 3000));
          }

          // Fetch prices for the batch in parallel
          const promises = batch.map(async (symbol) => {
            try {
              const url = getYahooFinanceUrl(symbol);
              console.log(`Fetching price for ${symbol} using URL:`, url);
              
              const response = await axios.get(url, {
                headers: {
                  'Accept': 'application/json'
                },
                timeout: 15000
              });
              
              // Parse the response data
              let data;
              try {
                data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
              } catch (parseError) {
                console.error(`Error parsing response for ${symbol}:`, parseError);
                return null;
              }

              console.log(`Response for ${symbol}:`, data);

              if (data?.chart?.result?.[0]) {
                const result = data.chart.result[0];
                const quote = result.meta;
                
                if (!quote) {
                  console.log(`No quote data for ${symbol}`);
                  return null;
                }

                const regularMarketPrice = quote.regularMarketPrice;
                const previousClose = quote.chartPreviousClose;
                
                if (regularMarketPrice && previousClose) {
                  const change = regularMarketPrice - previousClose;
                  const changePercent = (change / previousClose) * 100;

                  // Find the fund name associated with this symbol
                  const fund = fundsWithSymbols.find(inv => inv.symbol.trim() === symbol);
                  if (!fund) {
                    console.error(`No fund found for symbol ${symbol}`);
                    return null;
                  }

                  return {
                    fund_name: fund.name,
                    symbol: symbol,
                    price: regularMarketPrice,
                    change,
                    changePercent,
                    currency: quote.currency || 'INR',
                    lastUpdated: new Date().toISOString()
                  };
                } else {
                  console.log(`Missing price data for ${symbol}:`, { regularMarketPrice, previousClose });
                }
              } else {
                console.log(`Invalid response format for ${symbol}:`, data);
              }
              return null;
            } catch (error) {
              console.error(`Error fetching price for ${symbol}:`, error.message);
              if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
              }
              return null;
            }
          });

          const results = await Promise.all(promises);
          console.log('Batch results:', results);
          
          results.forEach((result) => {
            if (result) {
              newPrices[result.fund_name] = {
                symbol: result.symbol,
                price: result.price,
                change: result.change,
                changePercent: result.changePercent,
                currency: result.currency,
                lastUpdated: result.lastUpdated
              };
              hasValidPrices = true;
            }
          });

        } catch (error) {
          console.error(`Error processing batch ${i/batchSize + 1}:`, error);
        }
      }

      if (hasValidPrices) {
        console.log('Successfully fetched prices:', newPrices);
        setLivePrices(newPrices);
        setLastPriceUpdate(new Date());

        // Calculate current total value
        const newTotalValue = investments.reduce((sum, investment) => {
          const livePrice = newPrices[investment.name]?.price;
          if (livePrice) {
            return sum + (investment.units * livePrice);
          }
          return sum + investment.value;
        }, 0);

        setCurrentTotalValue(newTotalValue);
      } else {
        const errorMsg = 'No valid prices could be fetched. Please check your symbol codes and try again.';
        console.error(errorMsg);
        setPriceError(errorMsg);
        setCurrentTotalValue(totalValue);
      }

    } catch (error) {
      console.error('Error in fetchLivePrices:', error);
      setPriceError('Failed to fetch prices. Please try again later.');
      setCurrentTotalValue(totalValue);
    } finally {
      setLivePriceLoading(false);
    }
  }, [investments, totalValue]);

  // Add handleRefreshPrices function
  const handleRefreshPrices = async () => {
    if (livePriceLoading) return; // Prevent multiple simultaneous requests
    
    // Reset states before starting
    setLivePrices({});
    setPriceError(null);
    setLastPriceUpdate(null);
    setLivePriceLoading(true);
    
    try {
      await fetchLivePrices();
    } catch (error) {
      console.error('Error refreshing prices:', error);
      setPriceError('Failed to refresh prices. Please try again.');
    } finally {
      setLivePriceLoading(false);
    }
  };

  // Add PriceLoadingIndicator component
  const PriceLoadingIndicator = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={16} />
      <Typography variant="caption" color="text.secondary">
        Fetching prices...
      </Typography>
    </Box>
  );

  // Add new handlers for symbol editing
  const handleEditSymbolClick = () => {
    handleTransactionMenuClose();
    if (!currentTransaction) return;
    
    setSelectedFundForSymbol(currentTransaction);
    setNewSymbol(currentTransaction.symbol || '');
    setOpenSymbolModal(true);
  };

  const handleSymbolModalClose = () => {
    setOpenSymbolModal(false);
    setSelectedFundForSymbol(null);
    setNewSymbol('');
  };

  const handleUpdateSymbol = async () => {
    if (!selectedFundForSymbol || !selectedFundForSymbol.name) {
      console.error('No fund selected or fund name missing for symbol update');
      alert('Cannot update symbol: Fund name missing.');
      return;
    }

    const fundNameToUpdate = selectedFundForSymbol.name;
    const symbolToUpdate = newSymbol.trim().toUpperCase();

    try {
      setLoading(true);
      
      console.log(`Attempting to update symbol for fund: ${fundNameToUpdate} to ${symbolToUpdate}`);

      // Update symbol in Supabase for ALL investments with this fund name
      const { error, count } = await supabase
        .from('mutual_fund_investments')
        .update({ symbol: symbolToUpdate })
        .eq('name', fundNameToUpdate) // Use name for query
        .select(); // select() to get count of updated rows

      if (error) {
        console.error('Error updating symbol:', error);
        throw error;
      } 
      
      console.log(`Successfully updated ${count || 0} records for fund ${fundNameToUpdate}`);

      // Refresh the data
      await fetchInvestments();
      
      // Close the modal
      handleSymbolModalClose();
      
      // Show success message
      alert(`Symbol updated successfully for all '${fundNameToUpdate}' investments.`);
      
    } catch (error) {
      console.error('Error updating symbol:', error);
      alert(`Failed to update symbol: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Add handleFundNameChange function after handleReset
  const handleFundNameChange = (event, newValue) => {
    setFundName(newValue);
    
    // If an existing fund is selected, populate the symbol
    if (newValue && typeof newValue === 'string') {
      const selectedExistingFund = investments.find(fund => fund.name === newValue);
      if (selectedExistingFund && selectedExistingFund.symbol) {
        setFundSymbol(selectedExistingFund.symbol);
      } else {
        // Clear symbol if selected fund has no symbol or it's a new entry
        setFundSymbol('');
      }
    } else {
      // Clear symbol if input is cleared or not a string (e.g., null)
      setFundSymbol('');
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    const sortedData = [...investments].sort((a, b) => {
      // Fund Name
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      // Invested Value
      if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      }
      // Current Value
      if (key === 'currentValue') {
        const liveA = livePrices[a.name]?.price;
        const liveB = livePrices[b.name]?.price;
        const currA = liveA ? a.units * liveA : a.value;
        const currB = liveB ? b.units * liveB : b.value;
        return direction === 'asc' ? currA - currB : currB - currA;
      }
      // P/L
      if (key === 'pl') {
        const liveA = livePrices[a.name]?.price;
        const liveB = livePrices[b.name]?.price;
        const currA = liveA ? a.units * liveA : a.value;
        const currB = liveB ? b.units * liveB : b.value;
        const plA = currA - a.value;
        const plB = currB - b.value;
        return direction === 'asc' ? plA - plB : plB - plA;
      }
      // Invested %
      if (key === 'investedPct') {
        const pctA = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
        const pctB = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
        return direction === 'asc' ? pctA - pctB : pctB - pctA;
      }
      // Current %
      if (key === 'currentPct') {
        const liveA = livePrices[a.name]?.price;
        const liveB = livePrices[b.name]?.price;
        const currA = liveA ? a.units * liveA : a.value;
        const currB = liveB ? b.units * liveB : b.value;
        const pctA = currentTotalValue > 0 ? (currA / currentTotalValue) * 100 : 0;
        const pctB = currentTotalValue > 0 ? (currB / currentTotalValue) * 100 : 0;
        return direction === 'asc' ? pctA - pctB : pctB - pctA;
      }
      return 0;
    });
    setInvestments(sortedData);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/investments/cash-and-liquid')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box data-tour="inv-prod-summary">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Mutual Funds
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
                Invested: {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                maximumFractionDigits: 0,
              }).format(totalValue)}
            </Typography>
              {livePriceLoading ? (
                <PriceLoadingIndicator />
              ) : priceError ? (
                <Chip
                  icon={<ErrorIcon />}
                  label={priceError}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                />
              ) : Object.keys(livePrices).length > 0 ? (
                <>
                  <Typography variant="subtitle1" color="text.secondary">
                    | Current Value: {new Intl.NumberFormat('en-IN', {
                      style: 'currency',
                      currency: 'INR',
                      maximumFractionDigits: 0,
                    }).format(currentTotalValue)}
                  </Typography>
                  {lastPriceUpdate && (
                    <Tooltip title={`Last updated: ${lastPriceUpdate.toLocaleTimeString()}`}>
                      <Chip
                        icon={<AccessTimeIcon />}
                        label="Live"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </>
              ) : (
                <Chip
                  icon={<ErrorIcon />}
                  label="No live prices available"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Button
            data-tour="inv-prod-live"
            variant="outlined"
            startIcon={livePriceLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleRefreshPrices}
            disabled={livePriceLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 140,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {livePriceLoading ? 'Updating...' : 'Get Live NAV'}
          </Button>
          
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={fetchInvestments} 
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
              }}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button 
            data-tour="inv-prod-add"
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2,
            }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>

      {/* Modal for adding new investment */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-investment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Add New Mutual Fund Investment
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Autocomplete
                value={fundName}
                onChange={handleFundNameChange}
                freeSolo
                autoHighlight
                options={userFundNames}
                getOptionLabel={(option) => {
                  // Handle both string values and objects
                  return typeof option === 'string' ? option : option;
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter(option => 
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  
                  // Add the user's input as an option if it's not already in the list
                  if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
                    filtered.push(params.inputValue);
                  }
                  
                  return filtered;
                }}
                groupBy={(option) => {
                  // Custom entered value
                  if (!userFundNames.includes(option)) {
                    return "Custom Entry";
                  }
                  // User's existing funds
                  else {
                    return "Your Fund Names";
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Name of Fund" 
                    required 
                    fullWidth
                    placeholder="Enter or select fund name"
                    helperText="You can type a new fund name or select from your existing funds"
                  />
                )}
              />
              
              <TextField
                label="Symbol Code"
                value={fundSymbol}
                onChange={(e) => setFundSymbol(e.target.value.toUpperCase())}
                required
                fullWidth
                placeholder="Enter fund symbol (e.g., 0P0000ABCD)"
                helperText="Enter the fund's symbol code for live NAV updates"
                inputProps={{
                  style: { textTransform: 'uppercase' }
                }}
              />
              
              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="NAV"
                value={navValue}
                onChange={(e) => setNavValue(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Amount Invested"
                value={amountInvested}
                onChange={(e) => setAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={units}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount / NAV)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Transaction Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleTransactionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 150, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" color="primary" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
          <DeleteIcon fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Transaction Modal */}
      <Modal
        open={openEditModal}
        onClose={handleEditModalClose}
        aria-labelledby="edit-transaction-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Transaction
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Fund: {currentTransaction?.name}
              </Typography>
              
              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="NAV Value"
                value={editNavValue}
                onChange={(e) => setEditNavValue(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Amount Invested"
                value={editAmountInvested}
                onChange={(e) => setEditAmountInvested(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={numberInputStyle}
              />
              
              <TextField
                label="Number of Units"
                value={editUnits}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                helperText="Auto-calculated (Amount / NAV)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-transaction-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Typography variant="body2">
                Fund: {currentTransaction.name}<br />
                Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(currentTransaction.value)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTransaction} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal for viewing transaction history */}
      <Modal
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        aria-labelledby="transaction-history-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: 900 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {selectedFund?.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'primary.main' }}
              >
                Symbol: {selectedFund?.symbol || 'Not set'} |
                Invested Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(selectedFund?.value || 0)} |
                Total Units: {(selectedFund?.units || 0).toFixed(2)} |
                Average NAV: ₹{(() => {
                  if (!fundTransactions || fundTransactions.length === 0) return '0.00';
                  const totalAmount = fundTransactions.reduce((sum, tx) => sum + tx.value, 0);
                  const totalUnits = fundTransactions.reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0);
                  return totalUnits > 0 ? (totalAmount / totalUnits).toFixed(2) : '0.00';
                })()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedFundForSymbol(selectedFund);
                  setNewSymbol(selectedFund?.symbol || '');
                  setOpenSymbolModal(true);
                  handleCloseHistoryModal();
                }}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Edit Symbol
              </Button>
              <IconButton onClick={handleCloseHistoryModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Chip 
              icon={<ReceiptIcon fontSize="small" />} 
              label="Transaction History" 
              color="primary" 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Date</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>Fund Name</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>Invested Value</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>NAV</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>Units</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundTransactions.length > 0 ? (
                  fundTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {transaction.purchaseDate ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN') : '-'}
                      </TableCell>
                      <TableCell>
                        {transaction.name}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(transaction.value)}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.navValue 
                          ? transaction.navValue.toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.units 
                          ? parseFloat(transaction.units).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      No transaction history available
                    </TableCell>
                  </TableRow>
                )}
                {fundTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(fundTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                    </TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fundTransactions
                        .reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0)
                        .toFixed(2)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={handleCloseHistoryModal}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Symbol Edit Modal */}
      <Modal
        open={openSymbolModal}
        onClose={handleSymbolModalClose}
        aria-labelledby="edit-symbol-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Fund Symbol
          </Typography>
          
          <Stack spacing={3}>
            <Typography variant="subtitle1">
              Fund: {selectedFundForSymbol?.name}
            </Typography>
            
            <TextField
              label="Symbol Code"
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
              fullWidth
              placeholder="Enter fund symbol (e.g., 0P0000ABCD)"
              helperText="Enter the fund's symbol code for live NAV updates"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleSymbolModalClose}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={handleUpdateSymbol}
                disabled={loading}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                {loading ? 'Updating...' : 'Update Symbol'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Box
            component="img"
            src="/empty-state.svg"
            alt="No investments"
            sx={{ width: 200, height: 200, mb: 3, opacity: 0.7 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Mutual Funds Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
            Start by adding your first mutual fund investment using the 'Add Investment' button.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Add Your First Investment
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-prod-holdings"
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Fund Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('name')}
                      >
                        Fund Name
                        {sortConfig.key === 'name' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('value')}
                      >
                        Invested Value
                        {sortConfig.key === 'value' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('currentValue')}
                      >
                        Current Value
                        {sortConfig.key === 'currentValue' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('pl')}
                      >
                        P/L
                        {sortConfig.key === 'pl' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('investedPct')}
                      >
                        Invested %
                        {sortConfig.key === 'investedPct' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}
                        onClick={() => handleSort('currentPct')}
                      >
                        Current %
                        {sortConfig.key === 'currentPct' && (
                          <span style={{ marginLeft: 8 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => {
                      const livePrice = livePrices[investment.name];
                      const currentValue = livePrice ? investment.units * livePrice.price : investment.value;
                      const changePercent = livePrice ? livePrice.changePercent : 0;
                      const investedPercentage = ((investment.value / totalValue) * 100).toFixed(1);
                      const currentPercentage = ((currentValue / currentTotalValue) * 100).toFixed(1);
                      const profitLoss = currentValue - investment.value;
                      const profitLossPercent = ((profitLoss / investment.value) * 100).toFixed(1);
                      
                      return (
                      <TableRow
                        key={investment.name}
                        onClick={() => handleOpenTransactionHistory(investment)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: COLORS[index % COLORS.length],
                                mr: 2,
                              }}
                            />
                            {investment.name}
                              {livePrice && (
                                <Chip
                                  icon={changePercent >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                  label={`₹${livePrice.price.toFixed(2)} (${changePercent.toFixed(2)}%)`}
                                  color={changePercent >= 0 ? 'success' : 'error'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 2 }}
                                />
                              )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              maximumFractionDigits: 0,
                            }).format(currentValue)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{
                                color: profitLoss >= 0 ? 'success.main' : 'error.main',
                                fontWeight: 500,
                              }}
                            >
                              {profitLoss >= 0 ? '+' : ''}
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                                signDisplay: 'never'
                              }).format(Math.abs(profitLoss))}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: profitLoss >= 0 ? 'success.main' : 'error.main',
                                }}
                              >
                                {profitLoss >= 0 ? '+' : ''}{profitLossPercent}%
                              </Typography>
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {investedPercentage}%
                          </TableCell>
                          <TableCell align="right">
                            {currentPercentage}%
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View transactions">
                            <IconButton size="small" sx={{ color: 'primary.main' }}>
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    <TableRow
                      sx={{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                        '& td': { borderBottom: '2px solid', borderColor: 'divider' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(currentTotalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {(() => {
                          // Show 0 P/L if no live prices are available
                          if (Object.keys(livePrices).length === 0) {
                            return (
                              <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                ₹0
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                  }}
                                >
                                  0.0%
                                </Typography>
                              </Typography>
                            );
                          }

                          const totalPL = currentTotalValue - totalValue;
                          const totalPLPercent = ((totalPL / totalValue) * 100).toFixed(1);
                          const isProfit = totalPL >= 0;
                          
                          return (
                            <Typography
                              sx={{
                                color: isProfit ? 'success.main' : 'error.main',
                                fontWeight: 600,
                              }}
                            >
                              {isProfit ? '+' : ''}
                              {new Intl.NumberFormat('en-IN', {
                                style: 'currency',
                                currency: 'INR',
                                maximumFractionDigits: 0,
                                signDisplay: 'never'
                              }).format(Math.abs(totalPL))}
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: isProfit ? 'success.main' : 'error.main',
                                }}
                              >
                                {isProfit ? '+' : ''}{totalPLPercent}%
                              </Typography>
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4} data-tour="inv-prod-charts">
            <Grid container spacing={2}>
              {/* Current Value Distribution */}
              <Grid item xs={12}>
            <Paper
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Current Distribution
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Based on live market prices
                </Typography>
              </Box>
              <Box
                sx={{
                      height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                }}
              >
                    {pieChartData.currentData.length > 0 && (
                  <PieChart colors={chartPalette}
                    series={[
                      {
                            data: pieChartData.currentData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                    ]}
                        height={300}
                        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                        slotProps={{ legend: { hidden: true } }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Invested Value Distribution */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: 'var(--fx-card-shadow)',
                          display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Invested Distribution
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Based on purchase values
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                    }}
                  >
                    {pieChartData.investedData.length > 0 && (
                      <PieChart colors={chartPalette}
                        series={[
                          {
                            data: pieChartData.investedData,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                            faded: { innerRadius: 30, additionalRadius: -30 },
                      },
                        ]}
                        height={300}
                        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                        slotProps={{ legend: { hidden: true } }}
                  />
                )}
              </Box>
            </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <GuidedTour steps={TOURS['inv-cash-and-liquid-mutual-fund']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default MutualFund;